import React from 'react';
import ResourceModal from './ResourceModal';
import { Separator } from "@/components/ui/separator";
import { FaRobot, FaImage, FaExclamationCircle, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';

interface SpottingDeepfakesGuideProps {
  onClose: () => void;
}

const SpottingDeepfakesGuide: React.FC<SpottingDeepfakesGuideProps> = ({ onClose }) => {
  return (
    <ResourceModal title="Spotting Deepfakes Guide" onClose={onClose}>
      <div className="space-y-6">
        <div className="flex items-center gap-4 bg-green-500/10 rounded-xl p-4">
          <div className="bg-green-500/20 p-3 rounded-full">
            <FaRobot className="text-green-400 h-8 w-8" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Guide Overview</h3>
            <p className="text-gray-400">Learn how to identify AI-generated or manipulated media</p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-bold mb-2">What Are Deepfakes?</h3>
          <p className="text-gray-300 mb-4">
            Deepfakes are synthetic media where a person's likeness is replaced with someone else's using artificial intelligence. 
            The term combines "deep learning" and "fake," referring to the deep learning techniques used to create these convincing forgeries.
          </p>
          <p className="text-gray-300">
            While this technology has legitimate uses in entertainment and art, it also poses significant risks when used to spread misinformation, 
            create non-consensual imagery, or impersonate public figures for malicious purposes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-black/30 border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <FaImage className="text-truth h-5 w-5" />
              <h4 className="font-semibold">Types of Deepfakes</h4>
            </div>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li>• Face swaps (replacing one person's face with another)</li>
              <li>• Lip synchronization (making it appear someone said something they didn't)</li>
              <li>• Full body puppetry (controlling someone's entire body movements)</li>
              <li>• Voice cloning (creating synthetic copies of someone's voice)</li>
              <li>• AI-generated text and imagery (completely fabricated content)</li>
            </ul>
          </div>
          
          <div className="bg-black/30 border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <FaExclamationCircle className="text-red-400 h-5 w-5" />
              <h4 className="font-semibold">Common Uses & Risks</h4>
            </div>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li>• Political manipulation and propaganda</li>
              <li>• Non-consensual intimate imagery</li>
              <li>• Financial fraud through voice impersonation</li>
              <li>• Undermining trust in authentic media</li>
              <li>• Creating false narratives or "evidence"</li>
            </ul>
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-bold mb-4">Telltale Signs of Deepfakes</h3>
          
          <div className="space-y-4">
            {[
              {
                title: "Unnatural Facial Features",
                description: "Look for blurry or distorted faces, especially around the eyes, mouth, and where the face meets the neck or hairline.",
                icon: <FaExclamationCircle className="text-red-400 h-5 w-5" />
              },
              {
                title: "Lighting Inconsistencies",
                description: "Watch for unnatural shadows, reflections that don't match the environment, or inconsistent lighting across the face.",
                icon: <FaExclamationCircle className="text-red-400 h-5 w-5" />
              },
              {
                title: "Unnatural Eye Movements",
                description: "Pay attention to eye blinking patterns (too frequent, too rare, or unnatural), lack of eye reflections, or mismatched eye movements.",
                icon: <FaExclamationCircle className="text-red-400 h-5 w-5" />
              },
              {
                title: "Audio-Visual Mismatch",
                description: "Notice if lip movements don't perfectly match the words being spoken, or if the voice doesn't seem to match the person speaking.",
                icon: <FaExclamationCircle className="text-red-400 h-5 w-5" />
              },
              {
                title: "Unusual Behavior or Background",
                description: "Watch for strange head positions, jerky movements, or backgrounds that show signs of manipulation or inconsistent elements.",
                icon: <FaExclamationCircle className="text-red-400 h-5 w-5" />
              },
              {
                title: "Digital Artifacts",
                description: "Look for glitches, flickering, strange color patches, or unnatural blurring around edges of objects or people.",
                icon: <FaExclamationCircle className="text-red-400 h-5 w-5" />
              }
            ].map((sign, index) => (
              <div key={index} className="flex gap-3 bg-black/20 border border-gray-800 rounded-lg p-4">
                <div className="flex-shrink-0 mt-1">{sign.icon}</div>
                <div>
                  <h4 className="font-medium">{sign.title}</h4>
                  <p className="text-gray-400 text-sm">{sign.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-bold mb-4">How to Verify Suspicious Media</h3>
          
          <ol className="space-y-4">
            {[
              {
                title: "Check the Source",
                description: "Verify if the content comes from a reputable source. If it's only appearing on obscure websites or accounts with limited history, be skeptical.",
                icon: <FaCheckCircle className="text-green-500 h-5 w-5" />
              },
              {
                title: "Reverse Image Search",
                description: "Use tools like Google Images, TinEye, or Bing Visual Search to find other instances of the image or video online and check its origin.",
                icon: <FaCheckCircle className="text-green-500 h-5 w-5" />
              },
              {
                title: "Use Deepfake Detection Tools",
                description: "Applications like Sensity AI, Microsoft Video Authenticator, or DeepWare can help identify potentially manipulated media.",
                icon: <FaCheckCircle className="text-green-500 h-5 w-5" />
              },
              {
                title: "Check Metadata",
                description: "Examine the file's metadata for information about when and how it was created or modified.",
                icon: <FaCheckCircle className="text-green-500 h-5 w-5" />
              },
              {
                title: "Cross-Reference with Other Coverage",
                description: "If it's a newsworthy event, check if other reliable news sources are reporting the same information.",
                icon: <FaCheckCircle className="text-green-500 h-5 w-5" />
              }
            ].map((step, index) => (
              <li key={index} className="flex gap-3 bg-black/20 border border-gray-800 rounded-lg p-4">
                <span className="bg-truth/20 text-truth h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </span>
                <div>
                  <h4 className="font-medium">{step.title}</h4>
                  <p className="text-gray-400 text-sm">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="bg-blue-900/20 border border-blue-900/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <FaInfoCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-blue-400 mb-1">Remember</h4>
              <p className="text-gray-300 text-sm">
                Deepfake technology is constantly evolving and becoming more sophisticated. While these guidelines can help identify many current deepfakes, 
                the most advanced ones may be virtually undetectable to the human eye. When in doubt, rely on verification from multiple trusted sources 
                and avoid sharing content when its authenticity cannot be confirmed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ResourceModal>
  );
};

export default SpottingDeepfakesGuide;
